/**
 * Binding: `PerformanceObserver`s for `longtask`, `long-animation-frame` and `event` entries,
 * forwarded unchanged to the Rust `PerfLog` (`crates/compute-scheduler/src/metrics.rs`), which
 * owns attribution, per-interaction latency, quantiles and the benchmark JSON. Exposed on
 * `window.__fluxPerf` for end-to-end tests and the responsiveness benchmark.
 *
 * Opt-in: development builds, builds made with `VITE_FLUX_PERF=1`, or a page where a test set
 * `window.__FLUX_PERF_ENABLE__ = true` before the app loaded (Playwright `addInitScript`). The
 * observers use `buffered: true`, so installing them after the WASM package loads still sees
 * entries from page start (within the browser's per-type buffer limits).
 *
 * @module apps/web/src/lib/compute/perf
 */

import type { ComputeModule, ComputeCoreHandle } from './wasm';

/** What `window.__fluxPerf` offers. */
export interface FluxPerf {
  /** Parsed Rust summary of entries starting at or after `since` (default: all). */
  summary(since?: number): unknown;
  /** `browser-responsiveness` benchmark JSON object for entries since `since`. */
  benchmark(since: number, scenario: string): unknown;
  /** Recorded long animation frames (diagnostics). */
  frames(): unknown;
  /** Compute scheduler telemetry snapshot. */
  compute(): unknown;
  /** Which entry types this browser supports. */
  supported: Record<'longtask' | 'long-animation-frame' | 'event', boolean>;
  /** Drop recorded entries. */
  reset(): void;
  /** Stop observing. */
  disconnect(): void;
}

/** Window with the instrumentation globals. */
type PerfWindow = typeof globalThis & {
  /** Installed instrumentation. */
  __fluxPerf?: FluxPerf;
  /** Test opt-in. */
  __FLUX_PERF_ENABLE__?: boolean;
};

/** Whether instrumentation is enabled for this build/page. */
export function responsivenessEnabled(): boolean {
  const env = (import.meta as { env?: Record<string, unknown> }).env ?? {};
  return (
    env.DEV === true ||
    env.VITE_FLUX_PERF === '1' ||
    (globalThis as PerfWindow).__FLUX_PERF_ENABLE__ === true
  );
}

/** Entry fields read from Long Animation Frame and Event Timing entries. */
type TimingEntry = PerformanceEntry & {
  /** LoAF blocking time. */
  blockingDuration?: number;
  /** LoAF scripts. */
  scripts?: Array<{
    /** Script URL. */
    sourceURL?: string;
    /** Script time. */
    duration: number;
  }>;
  /** Event Timing interaction id. */
  interactionId?: number;
};

/**
 * Install the observers once and expose `window.__fluxPerf`. Returns null when disabled, outside
 * a browser, or already installed.
 */
export function installResponsivenessObserver(
  module: ComputeModule,
  core: ComputeCoreHandle | undefined,
  force = false
): FluxPerf | null {
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') return null;
  const target = window as PerfWindow;
  if (target.__fluxPerf || (!force && !responsivenessEnabled())) return null;
  const log = new module.PerfLog(location.origin);
  const supports = (type: string) =>
    Boolean(PerformanceObserver.supportedEntryTypes?.includes(type));
  const observe = (type: string, onEntry: (entry: TimingEntry) => void, extra = {}) => {
    if (!supports(type)) return undefined;
    const observer = new PerformanceObserver(list => list.getEntries().forEach(onEntry));
    observer.observe({ type, buffered: true, ...extra } as PerformanceObserverInit);
    return observer;
  };
  const observers = [
    observe('longtask', entry => log.addLongTask(entry.startTime, entry.duration)),
    observe('long-animation-frame', entry => {
      const scripts = entry.scripts ?? [];
      log.addFrame(
        entry.startTime,
        entry.duration,
        entry.blockingDuration ?? 0,
        scripts.map(script => (script.sourceURL ?? '').replace(/\n/g, ' ')).join('\n'),
        Float64Array.from(scripts, script => script.duration)
      );
    }),
    // 16 ms is the smallest threshold allowed; the 104 ms default would hide fast interactions.
    observe(
      'event',
      entry => log.addEvent(entry.interactionId ?? 0, entry.name, entry.startTime, entry.duration),
      { durationThreshold: 16 }
    ),
  ];
  const perf: FluxPerf = {
    summary: (since = Number.NEGATIVE_INFINITY) => JSON.parse(log.summary(since)),
    benchmark: (since, scenario) =>
      core
        ? JSON.parse(log.benchmark(since, scenario, core))
        : { suite: 'browser-responsiveness', metrics: [] },
    frames: () => JSON.parse(log.frames()),
    compute: () => (core ? JSON.parse(core.snapshot()) : null),
    supported: {
      longtask: supports('longtask'),
      'long-animation-frame': supports('long-animation-frame'),
      event: supports('event'),
    },
    reset: () => log.clear(),
    disconnect: () => observers.forEach(observer => observer?.disconnect()),
  };
  target.__fluxPerf = perf;
  return perf;
}
